import {AppActions, RootState} from 'app/rootReducer';
import Toast from 'components/Basic/Toast';
import {userActions} from 'features/User';
import {UserRoles} from 'interfaces';
import {Epic, ofType} from 'redux-observable';
import {concat, from, of} from 'rxjs';
import {
  catchError,
  filter,
  ignoreElements,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {StripeService} from 'services';

import {paymentActions} from '../paymentSlice';

export const annualPlanInterestEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(paymentActions.annualPlanInterest.match),
    mergeMap(({payload: {paymentPlan}}) =>
      from(StripeService.annualPlanInterest({paymentPlan})).pipe(
        mergeMap(({data: {message}}) => [
          paymentActions.annualPlanInterestSuccess(),
          paymentActions.setManageSubscriptionState({
            subscriptionState: 'upgrade',
            data: message,
          }),
        ]),
        catchError((message: string) =>
          concat(
            of(paymentActions.annualPlanInterestFailure()),
            of(
              userActions.setAsyncError({
                filter: 'payment',
                message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

export const setPauseSubscriptionEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(paymentActions.pauseSubscription.match),
    mergeMap(() =>
      from(StripeService.pauseSubscription()).pipe(
        mergeMap(({data: {message}}) => [
          userActions.checkSession(UserRoles.member),
          paymentActions.pauseSubscriptionSuccess(),
          paymentActions.setManageSubscriptionState({
            subscriptionState: 'pause',
            data: message * 1000,
          }),
        ]),
        catchError((message: string) =>
          concat(
            of(paymentActions.pauseSubscriptionFailure()),
            of(
              userActions.setAsyncError({
                filter: 'payment',
                message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

export const cancelSubscriptionEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    filter(paymentActions.cancelSubscription.match),
    switchMap(({payload: data}) =>
      from(StripeService.cancelSubscription(data)).pipe(
        mergeMap(() => [
          paymentActions.cancelSubscriptionSuccess(),
          userActions.checkSession(UserRoles.member),
          userActions.resetAsyncError('payment'),
          paymentActions.resetManageSubscriptionState({
            subscriptionState: 'none',
          }),
        ]),
        catchError((message: string) =>
          concat(
            of(paymentActions.cancelSubscriptionFailure()),
            of(
              userActions.setAsyncError({
                filter: 'payment',
                message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

const getCurrentExperiencePlanEpic: Epic<AppActions, AppActions, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(paymentActions.getCurrentExperiencePlan.match),
    switchMap(() =>
      from(StripeService.getCurrentExperiencePlan()).pipe(
        withLatestFrom(state$),
        mergeMap(([result]) => [
          paymentActions.getCurrentExperiencePlanSuccess(result.data.data),
        ]),
        catchError((message: string) =>
          concat(
            of(paymentActions.getCurrentExperiencePlanFailure(message)),
            of(userActions.setAsyncError({filter: 'payment', message})),
          ),
        ),
      ),
    ),
  );

const subscribeToExperiencePlanEpic: Epic<AppActions, AppActions, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(paymentActions.subscribeToExperiencePlan.match),
    switchMap(({payload: {onSuccess, ...payload}}) =>
      from(StripeService.subscribeToExperiencePlan(payload)).pipe(
        withLatestFrom(state$),
        mergeMap(([result]) => {
          if (onSuccess) {
            onSuccess();
          }

          return [
            paymentActions.subscribeToExperiencePlanSuccess(result.data.data),
          ];
        }),
        catchError((message: string) =>
          concat(
            of(paymentActions.subscribeToExperiencePlanFailure(message)),
            of(userActions.setAsyncError({filter: 'payment', message})),
          ),
        ),
      ),
    ),
  );

const cancelExperiencePlanEpic: Epic<AppActions, AppActions, RootState> = (
  action$,
  state$,
) =>
  action$.pipe(
    filter(paymentActions.cancelExperiencePlan.match),
    switchMap(({payload: {onDone, ...payload}}) =>
      from(StripeService.cancelExperiencePlan(payload)).pipe(
        withLatestFrom(state$),
        mergeMap(([result]) => {
          if (onDone) {
            onDone();
          }

          return [paymentActions.cancelExperiencePlanSuccess(result.data.data)];
        }),
        catchError((message: string) => {
          if (onDone) {
            onDone();
          }

          return concat(
            of(paymentActions.cancelExperiencePlanFailure(message)),
            of(userActions.setAsyncError({filter: 'payment', message})),
          );
        }),
      ),
    ),
  );

/**
 * After the user logs-in, fetch their experience plan.
 */
export const refreshExperiencePlanEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = (action$, state$) =>
  action$.pipe(
    filter(
      action =>
        action.type === userActions.setUser.type ||
        action.type === userActions.checkSessionSuccess.type,
    ),
    withLatestFrom(state$),
    filter(([_, state]) => state.user.current?.role === UserRoles.member),
    map(() => paymentActions.getCurrentExperiencePlan()),
  );

/**
 * Epic for handling the errors which occur during experience plan actions.
 * @param action$ - actions
 * @param state$ - state
 */
export const experiencePlanActionFailuresEpic: Epic<
  AppActions,
  AppActions,
  RootState
> = action$ =>
  action$.pipe(
    ofType(
      paymentActions.getCurrentExperiencePlanFailure.type,
      paymentActions.subscribeToExperiencePlanFailure.type,
      paymentActions.cancelExperiencePlanFailure.type,
    ),
    tap(({payload}) => {
      Toast({
        type: 'error',
        message: payload as string,
      });
    }),
    ignoreElements(),
  );

export const manageSubscriptionEpics = [
  annualPlanInterestEpic,
  setPauseSubscriptionEpic,
  cancelSubscriptionEpic,
  getCurrentExperiencePlanEpic,
  subscribeToExperiencePlanEpic,
  cancelExperiencePlanEpic,
  refreshExperiencePlanEpic,
  experiencePlanActionFailuresEpic,
];
